import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	inject,
	input,
	OnDestroy,
	output,
	signal,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {
	IWorkOrder,
	IWorkOrderEditPayload,
} from '@app/domain/cmms-types/workorder-types';
import { NgxTolgeeModule, TranslateService } from '@tolgee/ngx';
import { EntityDescriptionComponent } from '../../entity-description/entity-description.component';
import { MatCardModule } from '@angular/material/card';
import { QuickEntityChipsComponent } from '../quick-entity-chips/quick-entity-chips.component';
import { Store } from '@ngrx/store';
import { startCreateIncident } from '@app/pages/cmms/components/incident/state/incidents.actions';
import {
	IIncident,
	IIncidentCreateVariables,
} from '@app/pages/cmms/interfaces/incidents.interface';
import { EntityEditTitleComponent } from '../../entity-edit-title/entity-edit-title.component';
import {
	formatAssetLocationToInput,
	formatCmmsLocationsToInput,
} from '@app/domain/locations/type-translations';
import { startCreateRequest } from '@app/pages/cmms/components/request/state/requests.action';
import {
	IRequest,
	IRequestCreateVariables,
} from '@app/pages/cmms/interfaces/request.interfaces';
import { ICreateEntityVariables, IQuickEntityChipsData } from '@app/domain/types';
import { startCreateWorkOrder } from '@app/pages/cmms/components/work-order/state/work-order.actions';
import { ICmmsAsset } from '@app/pages/cmms/interfaces/assets.interfaces';
import { SummarizeRequestsQuery } from '@app/pages/cmms/graphql/requests.queries';
import { Apollo } from 'apollo-angular';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { FormFieldLabelComponent } from '../../../ui-components/form-field-label/form-field-label.component';
import { IAttachedFile } from '@app/pages/cmms/interfaces/files.interfaces';
import { AiAssetImageAnalysisCardComponent } from '@app/pages/cmms/components/ai-asset-image-analysis-card/ai-asset-image-analysis-card.component';
import { IAiAssetAnalysisResponse } from '@app/shared/services/image-analysis.service';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';

export interface IAiAnalysisInput {
	existingData?: ICmmsAsset;
	attachedFiles?: IAttachedFile[];
	onAnalysisCompleted?: (response: IAiAssetAnalysisResponse) => void;
	onLoadingStateChange?: (isLoading: boolean) => void;
	onFilesAdded?: (files: IAttachedFile[]) => void;
}

export enum ActionType {
	INCIDENT = 'incident',
	REQUEST = 'request',
	WORK_ORDER = 'work_order',
	AI_ANALYSIS = 'ai_analysis',
}

export interface IActionConfig {
	actionType: ActionType;
	iconName: string;
	translationKey: string;
}

export const ACTION_CONFIG: IActionConfig[] = [
	{
		actionType: ActionType.WORK_ORDER,
		iconName: 'work_order',
		translationKey: 'work_order',
	},
	{
		actionType: ActionType.INCIDENT,
		iconName: 'incident',
		translationKey: 'incident',
	},
	{
		actionType: ActionType.REQUEST,
		iconName: 'request',
		translationKey: 'request',
	},
	{
		actionType: ActionType.AI_ANALYSIS,
		iconName: 'smart-stars',
		translationKey: 'ai_image_upload_title',
	},
];

@Component({
	selector: 'attach-new-quick-entity',
	imports: [
		MatIconModule,
		NgxTolgeeModule,
		MatButtonModule,
		EntityDescriptionComponent,
		MatCardModule,
		QuickEntityChipsComponent,
		EntityEditTitleComponent,
		MatProgressSpinnerModule,
		MatChipsModule,
		FormFieldLabelComponent,
		AiAssetImageAnalysisCardComponent,
	],
	templateUrl: './attach-new-quick-entity.component.html',
	styleUrl: './attach-new-quick-entity.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AttachNewQuickEntityComponent implements AfterViewInit, OnDestroy {
	private readonly store$ = inject(Store);
	private readonly translateService = inject(TranslateService);
	private readonly apollo = inject(Apollo);
	readonly featureFlagService = inject(FeatureFlagService);

	parentEntityData = input<IWorkOrder | IIncident | IRequest | ICmmsAsset>();
	shownActions = input<ActionType[]>([]);
	aiAnalysisInput = input<IAiAnalysisInput>();
	createdEntity = output<void>();

	currentCard = signal<ActionType>(null);
	entityName = signal<string>('');
	entityDescription = signal<string>('');
	entityChipData = signal<IQuickEntityChipsData>({
		priority: 2,
		assignee: null,
		labels: [],
		category: null,
	});
	summarizeRequestsResult = signal<{ description: string; subject: string }>({
		description: null,
		subject: null,
	});
	loading = signal<boolean>(false);

	availableForSummarizer = computed<boolean>(() => {
		if ('comments' in this.parentEntityData()) {
			const hasComments: IWorkOrder | IIncident | IRequest =
				this.parentEntityData() as IRequest;
			return 'requestId' in this.parentEntityData() && hasComments.comments?.length > 0;
		}
		return false;
	});

	readonly actionConfig = ACTION_CONFIG;

	filteredActionConfigs = computed<IActionConfig[]>(() => {
		let shownActions = this.shownActions();

		if (
			shownActions.includes(ActionType.AI_ANALYSIS) &&
			!this.featureFlagService.isFFEnabled('aiAssetImageAnalysis')
		) {
			shownActions = shownActions.filter(action => action !== ActionType.AI_ANALYSIS);
		}

		return this.actionConfig.filter(config => shownActions.includes(config.actionType));
	});

	public readonly ActionType = ActionType;

	constructor() {
		effect(() => {
			if (this.currentCard() === null) {
				return;
			}
			if (this.availableForSummarizer()) {
				if (!this.summarizeRequestsResult().subject) {
					this.summarizeRequest();
				}
				this.entityDescription.set(this.summarizeRequestsResult().description);
				this.entityName.set(this.summarizeRequestsResult().subject);
				return;
			}

			const entityType = this.translateService.instant(this.currentCard());
			//tolgee adds zero-width joiner and zero-width non-joiner characters to the string, we need to filter it out
			const cleanString = entityType.replace(/[\u200C\u200D]/g, '');

			this.entityName.set(cleanString + ' ' + this.parentEntityData()?.name);
		});
	}

	ngAfterViewInit() {
		if ('location' in this.parentEntityData()) {
			this.entityChipData.set({
				priority: 2,
				assignee: null,
				labels: this.parentEntityData().labels,
				category: null,
			});
			return;
		}
		const hasLocations: IWorkOrder | IIncident | IRequest = this.parentEntityData() as
			| IWorkOrder
			| IIncident
			| IRequest;
		this.entityChipData.set({
			priority: hasLocations.priority,
			assignee: hasLocations.assignee,
			labels: hasLocations.labels,
			category: hasLocations.category,
		});
	}

	onSave() {
		let variables: ICreateEntityVariables = {
			name: this.entityName(),
			description: this.entityDescription(),
			priority: this.entityChipData().priority,
			assignee: this.entityChipData().assignee?.psId ?? null,
			labels: this.entityChipData().labels.map(label => label.pk),
			category: this.entityChipData().category,
		};
		if ('locations' in this.parentEntityData()) {
			variables = {
				...variables,
				locations: formatCmmsLocationsToInput(
					(this.parentEntityData() as IIncident | IRequest).locations
				),
			};
		}
		if ('location' in this.parentEntityData()) {
			variables = {
				...variables,
				locations: formatAssetLocationToInput(
					(this.parentEntityData() as ICmmsAsset).location
				),
				assets: [
					{
						name: this.parentEntityData().name,
						id: this.parentEntityData().pk,
					},
				],
			};
		}
		if ('workOrderId' in this.parentEntityData() && this.currentCard() === 'work_order') {
			variables = { ...variables, parentWorkOrder: this.parentEntityData().pk };
		} else if ('workOrderId' in this.parentEntityData()) {
			variables = { ...variables, workOrders: [this.parentEntityData().pk] };
		}
		if ('requestId' in this.parentEntityData()) {
			variables = { ...variables, requests: [this.parentEntityData().pk] };
		}
		if ('incidentId' in this.parentEntityData()) {
			variables = { ...variables, incidents: [this.parentEntityData().pk] };
		}

		if (this.currentCard() === ActionType.INCIDENT) {
			this.createIncident(variables);
		} else if (this.currentCard() === ActionType.REQUEST) {
			this.createRequest(variables);
		} else if (this.currentCard() === ActionType.WORK_ORDER) {
			this.createWorkOrder(variables);
		}
	}

	createIncident(variables: ICreateEntityVariables) {
		const incidentVariables: IIncidentCreateVariables = {
			...variables,
		};
		this.store$.dispatch(
			startCreateIncident({
				variables: incidentVariables,
				closeSidebar: false,
				skipTableReload: true,
				dexterSource: null,
			})
		);
		this.createdEntity.emit();
		this.resetCard();
	}

	createRequest(variables: ICreateEntityVariables) {
		const requestVariables: IRequestCreateVariables = {
			assigneeType: 'profile',
			assets: [],
			...variables,
		};
		this.store$.dispatch(
			startCreateRequest({
				variables: requestVariables,
				skipTableReload: false,
				closeSidebar: false,
			})
		);
		this.createdEntity.emit();
		this.resetCard();
	}

	createWorkOrder(variables: ICreateEntityVariables) {
		const workOrderVariables: IWorkOrderEditPayload = {
			routineData: null,
			assigneeType: 'profile',
			approver: null,
			approverType: null,
			deadline: null,
			...variables,
		};
		this.store$.dispatch(
			startCreateWorkOrder({
				workOrder: workOrderVariables,
				skipTableReload: false,
				closeSidebar: false,
			})
		);
		this.createdEntity.emit();
		this.resetCard();
	}

	resetCard() {
		this.currentCard.set(null);
		this.entityName.set('');
		this.entityDescription.set('');
	}

	summarizeRequest() {
		if (!this.availableForSummarizer()) {
			return;
		}
		this.loading.set(true);
		this.apollo
			.query<{ summarizeRequest: { description: string; subject: string } }>({
				query: SummarizeRequestsQuery,
				variables: {
					objectId: this.parentEntityData().pk,
				},
				fetchPolicy: 'network-only',
			})
			.subscribe({
				next: (response: {
					data: { summarizeRequest: { description: string; subject: string } };
				}) => {
					this.loading.set(false);
					this.summarizeRequestsResult.set(response.data.summarizeRequest);
				},
			});
	}

	showCard(type: ActionType) {
		if (this.currentCard() === type) {
			this.currentCard.set(null);
		} else {
			this.currentCard.set(type);
		}
	}

	ngOnDestroy() {
		this.resetCard();
	}
}
